'use client';

import { Add } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { SetShowDialog, SetShowSnackbar } from '../../../types';
import { CountAndRate, LeasingRight } from '../../../types/leases';
import { DefaultEditModes } from '../../../utils';
import { isAdminDashboard } from '../../../utils/constants';
import { ConversionRight } from './conversionRight';

type ConversionRightsProps = {
	countAndRate: CountAndRate;
	leasingRight: LeasingRight;
	refetchLeasingRight: () => Promise<void>;
	countAndRateId: number | string;
	pageEditInProgress: boolean;
	conversionRightEditModes: DefaultEditModes;
	setConversionRightEditModes: (x: (y: DefaultEditModes) => DefaultEditModes) => void;
	setShowDialog: SetShowDialog;
	setShowSnackbar: SetShowSnackbar;
};

export const ConversionRights: FC<ConversionRightsProps> = ({
	countAndRate,
	leasingRight,
	refetchLeasingRight,
	countAndRateId,
	pageEditInProgress,
	conversionRightEditModes,
	setConversionRightEditModes,
	setShowDialog,
	setShowSnackbar,
}) => {
	const [conversionRights, setConversionRights] = useState(countAndRate.conversionRights);

	const countAndRateHasRightToConvert = useMemo(
		() => countAndRate.history.some((h) => h.rightToConvert),
		[countAndRate.history],
	);

	useEffect(() => {
		setConversionRights(countAndRate.conversionRights);
	}, [countAndRate]);

	const handleAdd = useCallback(() => {
		const newHistory = [
			{
				id: `new-${uuidv4()}`,
				startDate: countAndRate.history[0].startDate,
				conversionCount: 0,
				daysRequiredForNoticeToConvert: 0,
			},
		];

		const newConversionRight = {
			id: `new-${uuidv4()}`,
			rateName: '',
			sublotName: '',
			grossRatePrice: '',
			rateId: null,
			sublotId: null,
			history: newHistory,
		};

		setConversionRights((prev) => [newConversionRight, ...prev]);
		setConversionRightEditModes((prev) => ({ ...prev, [newConversionRight.id]: true }));
	}, [countAndRate.history, setConversionRightEditModes]);

	const handleCancelNewMode = useCallback(() => {
		setConversionRights((prev) => [
			...prev.reduce((acc, cr) => [...acc, ...(String(cr.id).includes('new') ? [] : [cr])], []),
		]);
	}, []);

	return (
		<Box ml='1em' mr='1em'>
			{conversionRights.length ? (
				<Box display='flex' flexDirection='row' gap='1em' mt='1.5em' mb='1em'>
					<Typography variant='h6' fontWeight='bold'>
						Conversion Rights
					</Typography>
					{!pageEditInProgress && isAdminDashboard && (
						<Button
							startIcon={<Add />}
							variant='contained'
							onClick={handleAdd}
							data-cy='add-conversion-right-button'
						>
							Add
						</Button>
					)}
				</Box>
			) : !pageEditInProgress && isAdminDashboard && countAndRateHasRightToConvert ? (
				<Button
					startIcon={<Add />}
					variant='outlined'
					onClick={handleAdd}
					data-cy='add-conversion-rights-button'
				>
					Add Conversion Rights
				</Button>
			) : null}
			<Box display='flex' flexDirection='column' gap='1em'>
				{conversionRights.map((conversionRight) => (
					<ConversionRight
						key={conversionRight.id}
						conversionRight={conversionRight}
						leasingRight={leasingRight}
						refetchLeasingRight={refetchLeasingRight}
						countAndRateId={countAndRateId}
						handleCancelNewMode={handleCancelNewMode}
						editMode={conversionRightEditModes[conversionRight.id]}
						setEditMode={(value: boolean) =>
							setConversionRightEditModes((prev) => ({ ...prev, [conversionRight.id]: value }))
						}
						pageEditInProgress={pageEditInProgress}
						setShowDialog={setShowDialog}
						setShowSnackbar={setShowSnackbar}
					/>
				))}
			</Box>
		</Box>
	);
};
